/**
 * @fileoverview Enhanced BacktestService with SSE support
 * Replaces polling with Server-Sent Events for real-time optimization updates
 */

import { apiClient } from '../api/client';
import { API_ENDPOINTS, type ApiTypes } from '../api/endpoints';
import type { OptimizationProgress } from '../../types/store';
import type { OptimizationJob } from './OptimizationEventService';
import { optimizationHistoryService } from './OptimizationHistoryService';
import type { HistoricalJobsOptions, BulkArchiveOptions } from './OptimizationHistoryService';
import { optimizationTracker } from './OptimizationTracker';
import type { TrackingOptions } from './OptimizationTracker';
import { optimizationJobManager } from './OptimizationJobManager';

export interface OptimizationOptions extends TrackingOptions {
  // Inherits: useSSE, fallbackToPoll, timeout, pollInterval
}

export class EnhancedBacktestService {

  /**
   * Start strategy optimization with SSE support
   */
  async startOptimization(
    request: ApiTypes.OptimizationRequest,
    _options: OptimizationOptions = {}
  ): Promise<ApiTypes.OptimizationResponse> {

    const response = await apiClient.post<{
      success?: boolean;
      data?: ApiTypes.OptimizationResponse;
      optimizationId?: string;
      error?: string | boolean;
      message?: string;
    }>(API_ENDPOINTS.OPTIMIZATION.START, request);

    // Check for error response first (backend returns {error: true, message: "..."} on error)
    if (response.error === true || (response.error && typeof response.error === 'string')) {
      const errorMessage = response.message || (typeof response.error === 'string' ? response.error : '') || 'Failed to start optimization';
      throw new Error(errorMessage);
    }

    // If there's no error field, treat response as success (optimizationId indicates success)
    // Backend may return {success: true, data: ...} or just {optimizationId: ...} directly

    // Handle different response formats
    if (response.data && response.optimizationId) {
      // Async mode: backend returns both data and optimizationId
      return {
        ...response.data,
        optimizationId: response.optimizationId,
        status: 'started'
      };
    } else if (response.data) {
      // Sync mode: backend returns only data (completed results)
      return response.data;
    } else if (response.optimizationId) {
      // Async mode: backend returns only optimizationId (direct response from WFA)
      // Return the response as-is, it already has the right shape
      return response as ApiTypes.OptimizationResponse;
    }

    throw new Error('Invalid optimization response format');
  }

  /**
   * Track optimization progress with SSE
   * Delegates to OptimizationTracker
   */
  async trackOptimizationProgress(
    optimizationId: string,
    onProgress: (progress: OptimizationProgress) => void,
    options: OptimizationOptions = {}
  ): Promise<OptimizationProgress> {
    return optimizationTracker.trackProgress(optimizationId, onProgress, options);
  }

  /**
   * Get current optimization jobs
   * Delegates to OptimizationJobManager
   */
  async getOptimizationJobs(): Promise<OptimizationJob[]> {
    return optimizationJobManager.getJobs();
  }

  /**
   * Cancel optimization job
   * Delegates to OptimizationJobManager
   */
  async cancelOptimization(optimizationId: string): Promise<void> {
    return optimizationJobManager.cancelJob(optimizationId);
  }

  /**
   * Remove completed optimization job
   * Delegates to OptimizationJobManager
   */
  async removeOptimizationJob(optimizationId: string): Promise<void> {
    return optimizationJobManager.removeJob(optimizationId);
  }

  /**
   * Archive (soft delete) a historical optimization job
   */
  /**
   * Archive (soft delete) a single historical optimization job
   * Delegates to OptimizationHistoryService
   */
  async archiveHistoricalJob(jobId: string): Promise<void> {
    return optimizationHistoryService.archiveJob(jobId);
  }

  /**
   * Archive (soft delete) a collection of historical optimization jobs
   * Delegates to OptimizationHistoryService
   */
  async archiveAllHistoricalJobs(options: BulkArchiveOptions = {}): Promise<{ archivedCount: number; matchedCount: number }> {
    return optimizationHistoryService.archiveBulk(options);
  }

  /**
   * Store completed optimization job in database for persistence
   * Delegates to OptimizationJobManager
   */
  async storeCompletedJob(job: OptimizationJob): Promise<void> {
    return optimizationJobManager.storeCompletedJob(job);
  }

  /**
   * Get historical completed jobs from database (optional for UI)
   * Delegates to OptimizationHistoryService
   */
  async getHistoricalJobs(options: HistoricalJobsOptions = {}): Promise<{
    jobs: OptimizationJob[];
    total: number;
    page: number;
    pageSize: number;
    totalPages?: number;
    hasMore?: boolean;
  }> {
    return optimizationHistoryService.getHistoricalJobs(options);
  }

  /**
   * Get optimization iteration count (for backwards compatibility)
   */
  async getIterationCount(): Promise<{ current: number; total: number }> {
    try {
      const response = await apiClient.get<{
        success: boolean;
        data: { current: number; total: number };
        error?: string;
      }>(API_ENDPOINTS.OPTIMIZATION.ITERATIONS);

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to get iteration count');
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

// Export singleton instance
export const enhancedBacktestService = new EnhancedBacktestService();